// @flow
import React, { useEffect, useRef, useState } from 'react';
import type { Node } from 'react';
import {
  Input,
  Link,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import useIntl from '../../hooks/useIntl';
import { formatDateLocale } from '../../utils/date-helpers';
import {
  formatMoney,
  formatPercent,
  varianceArrow,
  varianceColor,
} from './utils';
import messages from './messages';
import type { Amounts, Note } from './types';

/** The backend's limit on a note. */
export const NOTE_MAX_LENGTH = 4000;

type Emphasis = { strong: boolean };

const textVariant = (strong: boolean) => (strong ? 'labelMedium' : 'bodySmall');

export const AccountLabel = ({
  label,
  depth,
  strong,
}: { label: string, depth: number } & Emphasis): Node => (
  <Typography
    variant={textVariant(strong)}
    sx={{ display: 'block', pl: depth * 2, whiteSpace: 'nowrap' }}
  >
    {label}
  </Typography>
);

export const Money = ({
  value,
  strong,
}: { value: number } & Emphasis): Node => {
  const intl = useIntl();
  return (
    <Typography variant={textVariant(strong)} sx={{ whiteSpace: 'nowrap' }}>
      {formatMoney(intl, value)}
    </Typography>
  );
};

/** An actual amount that opens the GL entries behind it. */
export const DrillDownMoney = ({
  value,
  onClick,
}: {
  value: number,
  onClick: () => void,
}): Node => {
  const intl = useIntl();
  return (
    <Tooltip title={intl.formatMessage(messages.drillDownHint)}>
      <Link
        component="button"
        type="button"
        onClick={onClick}
        sx={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
      >
        {formatMoney(intl, value)}
      </Link>
    </Tooltip>
  );
};

export const Variance = ({
  amounts,
  strong,
}: { amounts: Amounts } & Emphasis): Node => {
  const intl = useIntl();
  return (
    <Typography
      variant={textVariant(strong)}
      sx={{ color: varianceColor(amounts.favorable), whiteSpace: 'nowrap' }}
    >
      {`${varianceArrow(amounts.variance)} ${formatMoney(
        intl,
        amounts.variance,
      )}`.trim()}
    </Typography>
  );
};

export const VariancePercent = ({
  amounts,
  strong,
}: { amounts: Amounts } & Emphasis): Node => {
  const intl = useIntl();
  return (
    <Typography
      variant={textVariant(strong)}
      sx={{ color: varianceColor(amounts.favorable), whiteSpace: 'nowrap' }}
    >
      {formatPercent(intl, amounts.variancePercent)}
    </Typography>
  );
};

/** A line's note. Saves when the field loses focus, if it changed. */
export const NoteCell = ({
  note,
  readOnly,
  saving = false,
  onSave,
}: {
  note: ?Note,
  readOnly: boolean,
  /** True while this note's save is in flight; the field is locked until it settles. */
  saving?: boolean,
  onSave: (value: string) => void,
}): Node => {
  const intl = useIntl();
  const saved = note?.note ?? '';
  const [draft, setDraft] = useState(saved);
  const lastSaved = useRef(saved);

  // Take the saved note only while the field still shows the previous saved note, so a save
  // that lands while someone is typing again does not erase the newer text.
  useEffect(() => {
    const previous = lastSaved.current;
    lastSaved.current = saved;
    setDraft((current) => (current === previous ? saved : current));
  }, [saved]);

  const editedBy =
    note?.updatedByName && note.updatedAt
      ? intl.formatMessage(messages.noteEditedBy, {
          name: note.updatedByName,
          date: formatDateLocale(new Date(note.updatedAt), 'MM/DD/YYYY'),
        })
      : '';

  if (readOnly) {
    return (
      <Tooltip title={editedBy}>
        <Typography variant="bodySmall" sx={{ whiteSpace: 'pre-wrap' }}>
          {saved}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={editedBy}>
      <Input
        value={draft}
        multiline
        fullWidth
        disabled={saving}
        inputProps={{ maxLength: NOTE_MAX_LENGTH }}
        placeholder={intl.formatMessage(messages.notePlaceholder)}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          if (!saving && draft.trim() !== saved) {
            onSave(draft.trim());
          }
        }}
      />
    </Tooltip>
  );
};
